//@ts-nocheck
import { RumInit } from '@maersk-global/telemetry-web-sdk/dist/index.cjs.js';
import { VoyageImoModel } from '../../src/models/voyage-imo-model';
import type { ForecastInfo } from '../../src/models/weather-model';
import { isOnline } from './helpers';

const rumConfiguration = (<any>window)["appenv"].rumConfig;
const isVesselOnCloud: boolean = (window as any).appenv?.vesselOnCloud;
const VesselDbVersion = (window as any).appenv?.indexedDBConfig?.vesselDbVersion;
const SyncDbVersion = (window as any).appenv?.indexedDBConfig?.vesselSyncDbVersion;
const MapsDbVersion = (window as any).appenv?.indexedDBConfig?.mapsDbVersion;

export const faro = RumInit({
    app: {
        name: rumConfiguration?.appName,
        version: (<any>window)["appenv"].appVersion,
    },
    apiKey: rumConfiguration?.apiKey, // This is api key for dev
    debug: false, // Shows the captured in console
    performanceInstrument: {
        ignoreEvents: [/^msal\./], // To ignore certain events pass a list of exact events names or regex
    },
    instrumentations: {
        documentload: false, // resourceFetch js, css, document enabled by default
        xhr: true, // enabled default
        fetch: true, // enabled default
        webvitals: true, // fid , fcp , ttfb,  lcp, cls, inp enabled default
        console: true, // default info, warn , error  enabled    && log, debug, trace not enabled // console: { //   disabledLevels: [LogLevel.TRACE] // },
        errors: false,
        interactions: false, // default click // interactions: { //   eventNames: ['click', 'mouseup', 'mousedown', 'keyup', 'keydown'] // }
    },
    traceLog: false, // Sends Traces to Loki ( For easy querying of traces)
});

/**
 * Capture RUM metrics for showing the installed app version
 */
export const captureInstalledAppVersionMetrics = () => {
    // Capture the app version number under RUM metrics after the new version is installed
    faro.api.pushEvent("installed-app-version", { appVersion: (<any>window)["appenv"].appVersion });
};

export const captureLatestForecastVersionMetrics = (forecast: ForecastInfo) => {
    faro.api.pushEvent("latest-forecast-version", {
        forecastVersion: forecast.name,
        forecastTimestamp: forecast.timestamp,
        forecastFrom: forecast.forecast_from,
        forecastTo: forecast.forecast_to,
        hasLowResData: `${forecast.hasLowResData}`,
        hasHighResData: `${forecast.hasHighResData}`,
    });
};

export type StarRouteWrWorkflowPayload = Record<string, string | number | boolean | null | undefined>;

type RumEventAttributes = Record<string, string>;

interface QueuedRumEvent {
    name: string;
    attributes: RumEventAttributes;
    queuedAtUtc: string;
}

export const RUM_EVENT_OUTBOX_KEY = 'rumWorkflowEventOutbox';
const RUM_EVENT_OUTBOX_LIMIT = 200;

// Faro event attributes must be Record<string, string> (same shape as latest-forecast-version);
// non-string values are not mapped by the collector, so stringify everything and use '' for missing values.
export const toRumEventAttributes = (payload: StarRouteWrWorkflowPayload): RumEventAttributes =>
    Object.fromEntries(
        Object.entries(payload).map(([key, value]) => [key, value === null || value === undefined ? '' : `${value}`]),
    );

const readRumEventOutbox = (): QueuedRumEvent[] => {
    try {
        const parsed = JSON.parse(localStorage.getItem(RUM_EVENT_OUTBOX_KEY) || '[]');
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const writeRumEventOutbox = (events: QueuedRumEvent[]) => {
    try {
        if (events.length === 0) {
            localStorage.removeItem(RUM_EVENT_OUTBOX_KEY);
        } else {
            localStorage.setItem(RUM_EVENT_OUTBOX_KEY, JSON.stringify(events.slice(-RUM_EVENT_OUTBOX_LIMIT)));
        }
    } catch (error) {
        console.warn('Failed to persist RUM event outbox', error);
    }
};

// The RUM transport has no offline queue: events pushed while the vessel is offline are dropped.
// Workflow events are therefore kept in localStorage until the app is online again.
export const flushRumEventOutbox = () => {
    if (!isOnline()) return;
    const queued = readRumEventOutbox();
    if (queued.length === 0) return;
    writeRumEventOutbox([]);

    for (let i = 0; i < queued.length; i++) {
        const event = queued[i];
        try {
            faro.api.pushEvent(event.name, {
                ...event.attributes,
                deliveredFromOutbox: 'true',
                queuedAtUtc: event.queuedAtUtc,
            });
        } catch (error) {
            console.warn(`Failed to flush RUM event outbox at: ${event.name}`, error);
            writeRumEventOutbox([...queued.slice(i), ...readRumEventOutbox()]);
            return;
        }
    }
};

const queueRumEvent = (name: string, attributes: RumEventAttributes) => {
    writeRumEventOutbox([...readRumEventOutbox(), { name, attributes, queuedAtUtc: new Date().toISOString() }]);
};

export const captureStarRouteWrWorkflowEvent = (eventName: string, payload: StarRouteWrWorkflowPayload = {}) => {
    const attributes = toRumEventAttributes({
        ...payload,
        timestampUtc: payload.timestampUtc ?? new Date().toISOString(),
    });

    if (!isOnline()) {
        queueRumEvent(eventName, attributes);
        return;
    }

    try {
        flushRumEventOutbox();
        faro.api.pushEvent(eventName, { ...attributes, deliveredFromOutbox: 'false' });
    } catch (error) {
        console.warn(`Failed to push Star Route WR workflow RUM event: ${eventName}`, error);
        queueRumEvent(eventName, attributes);
    }
};

if (typeof window !== 'undefined') {
    window.addEventListener('online', flushRumEventOutbox);
    flushRumEventOutbox();
}

//Setting custom RUM session attributes for imo and service worker version
export const customRUMSession = () => {
    const swVersion = sessionStorage.getItem('app_build_version');
    const isVesselUser = sessionStorage.getItem('isVesselUser');
    const lastSynced = localStorage.getItem('lastSyncedTime');
    const lastSyncedTime = lastSynced ? new Date(lastSynced).toISOString() : '';
    faro.api.setUser({
        id: isVesselOnCloud ? `${VoyageImoModel?.imo}` : `${(<any>window)["appenv"].vesselIMONumber}`,
    });
    faro.api.setSession({
        attributes: {
            "imo": isVesselOnCloud ? `${VoyageImoModel?.imo}` : `${(<any>window)["appenv"].vesselIMONumber}`,
            "serviceWorkerVersion": isVesselOnCloud ? swVersion : '',
            "isVesselUser": isVesselOnCloud ? isVesselUser: '',
            "VesselDbVersion": isVesselOnCloud ? `${VesselDbVersion}`: '',
            "SyncDbVersion": isVesselOnCloud ? `${SyncDbVersion}`: '',
            "MapsDbVersion": isVesselOnCloud ? `${MapsDbVersion}`: '',
            "LastSynced": isVesselOnCloud ? `${lastSyncedTime}`: ''
        },
    });
}

// TODO: VVPC need to set this using the VesselIMOModel.
export const setImoInRealUserMonitoring = () => {
    // Capture the app version number under RUM metrics after the new version is installed
    faro.api.setUser({
        id: `${(<any>window)["appenv"].vesselIMONumber}`,
        attributes: {
            "imo": `${(<any>window)["appenv"].vesselIMONumber}`,
        },
    });
};
